import React from "react";
import { Button, Form, FormInstance, Input, Select, Space, Typography } from "antd";
import { ExportFile } from "assets/icons";
import Layout from "components/layout";
import { DatePicker } from "screens/private/report/components/datePicker";
import { IlocationByStructureId, IStructuresAllData } from "store/location/location-model";
import { IItems } from "store/report/report-model";
import { IUserData } from "store/user-session/user-session-model";
import { rulesPlates, validationSpecialCharacters } from "utils/form/validations";
import { maxRangeDate, validationCharacters } from "utils/functions";
import useTranslation from "utils/hooks/useTranslation";
import { getOptions, rangeConfig, translateDatePicker } from "../components/datePicker/configs";
import * as S from "./../components/styled";
import ReportGrid from "./grid";
import ColumnsTable from "./grid/columnsTableReport";

interface IPropsRenderReport {
  isLoading: boolean;
  isLoadingRegisters: boolean;
  userData: IUserData;
  locationStructure: IStructuresAllData;
  value: string[];
  formReport: FormInstance;
  locationByStructureIdList: IlocationByStructureId[];
  allowView: boolean;
  structuresAllList: IStructuresAllData[];
  dateValue: any[];
  dates: any[];
  maxDaysSelect: number;
  items: Array<IItems>;
  totalItems: number;
  pageIndex: number;
  inboundDocumentsTypes: string[];
  outboundDocumentsTypes: string[];
  onFinish: (...args) => void;
  onChangeStructureSelect: (...args) => void;
  onOpenChangeDatePicker: (...args) => void;
  setDateValue: React.Dispatch<React.SetStateAction<any[]>>;
  setValue: (...args) => void;
  setDates: React.Dispatch<React.SetStateAction<any[]>>;
  onReportQuery: (...args) => void;
  reportChangeFieldValue: (...args) => void;
  reportFetchRegisterAsync: (...args) => void;
}

const RenderReport = ({
  isLoading,
  isLoadingRegisters,
  userData,
  locationStructure,
  value,
  formReport,
  locationByStructureIdList,
  allowView,
  structuresAllList,
  dateValue,
  dates,
  maxDaysSelect,
  items,
  totalItems,
  pageIndex,
  inboundDocumentsTypes,
  outboundDocumentsTypes,
  onFinish,
  setValue,
  onOpenChangeDatePicker,
  onChangeStructureSelect,
  setDateValue,
  setDates,
  onReportQuery,
  reportChangeFieldValue,
  reportFetchRegisterAsync,
}: IPropsRenderReport): JSX.Element => {
  const { RangePicker } = DatePicker;
  const { Option } = Select;
  const { Title } = Typography;
  const TITLE_PAGE = useTranslation({ id: "inbound_and_outbound_report" });
  const MAXIMUM_PERIOD_TO_SELECT = useTranslation({ id: "maximum_period_to_select" });
  const STRUCTURE = useTranslation({ id: "structure" });
  const UNIT = useTranslation({ id: "unit" });
  const ALL = useTranslation({ id: "all" });
  const PLATE = useTranslation({ id: "plate" });
  const TRAILERS_PLATES = useTranslation({ id: "trailers_plates" });
  const DOCUMENT = useTranslation({ id: "document_fo" });
  const DATE = useTranslation({ id: "date" });
  const START_DATE = useTranslation({ id: "start_date" });
  const END_DATE = useTranslation({ id: "end_date" });
  const FILTER = useTranslation({ id: "filter" });
  const EXPORT = useTranslation({ id: "export" });

  return (
    <Layout title={TITLE_PAGE} isLoading={isLoading} role={userData.role}>
      <Form onFinish={onFinish} form={formReport} initialValues={{ structure: locationStructure?.description }}>
        <S.ReportContainer>
          <S.FilterContainerPrimary>
            <S.SelectStructureContainer>
              <Title level={5} style={S.ReportTitle}>
                {STRUCTURE}
              </Title>
              <Form.Item name="structure">
                <Select
                  size="large"
                  placeholder={locationStructure?.description}
                  disabled={allowView}
                  style={S.StructureSelect}
                  onChange={(structure) => onChangeStructureSelect(structure)}
                  data-testid="select-option"
                >
                  {structuresAllList.length > 1 &&
                    structuresAllList.map((option, index) => (
                      <Option value={JSON.stringify(option)} key={`select-options-structure-${index}`}>
                        {option.description}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </S.SelectStructureContainer>
            <S.LocationsContainer>
              <Title level={5} style={S.ReportTitle}>
                {UNIT}
              </Title>
              <Form.Item name="location">
                <Space direction="vertical" style={S.LocationSelect}>
                  <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    value={value}
                    onChange={(newValue: string[]) => {
                      setValue(newValue);
                    }}
                    placeholder={ALL}
                    maxTagCount="responsive"
                    disabled={allowView}
                  >
                    {locationByStructureIdList.length > 0 &&
                      locationByStructureIdList.map((item, index) => (
                        <Option key={`select-options-location-${index}`} value={item.description}>
                          {item.description}
                        </Option>
                      ))}
                  </Select>
                </Space>
              </Form.Item>
            </S.LocationsContainer>
            <S.FilterCarrierContainer>
              <Title level={5} style={S.ReportTitle}>
                {DOCUMENT}
              </Title>
              <S.FormItem name="document" data-testid="filter-document">
                <Input allowClear style={S.InputFields} maxLength={30} />
              </S.FormItem>
            </S.FilterCarrierContainer>
          </S.FilterContainerPrimary>
          <S.FilterReportContainerSecondary>
            <S.FilterPlateContainer>
              <Title level={5} style={S.ReportTitle}>
                {PLATE}
              </Title>
              <S.FormItem name="plate" data-testid="filter-plate" rules={rulesPlates()}>
                <Input
                  maxLength={7}
                  allowClear
                  className="inputUpper"
                  onKeyPress={(e) => {
                    validationCharacters(e, validationSpecialCharacters);
                  }}
                  style={S.InputFields}
                />
              </S.FormItem>
            </S.FilterPlateContainer>
            <S.FilterPlateContainer>
              <Title level={5} style={S.ReportTitle}>
                {TRAILERS_PLATES}
              </Title>
              <S.FormItem data-testid="filter-trailer-plate" name="trailerPlate" rules={rulesPlates()}>
                <Input
                  className="inputUpper"
                  allowClear
                  maxLength={7}
                  onKeyPress={(e) => {
                    validationCharacters(e, validationSpecialCharacters);
                  }}
                  style={S.InputFields}
                />
              </S.FormItem>
            </S.FilterPlateContainer>
            <S.DatePickerContainer>
              <Title level={5} style={S.ReportTitle}>
                {DATE}
              </Title>
              <Space direction="vertical" size={12} style={S.SpaceDatePicker}>
                <Form.Item name="rangePicker" {...rangeConfig()} data-testid="datePickerSubmit">
                  <RangePicker
                    size="large"
                    format="DD/MM/YYYY"
                    disabledDate={(current) => maxRangeDate(current, dates, maxDaysSelect)}
                    value={(dateValue[0], dateValue[1])}
                    onCalendarChange={(val) => setDates(val)}
                    onChange={(val) => setDateValue(val)}
                    onOpenChange={onOpenChangeDatePicker}
                    allowClear={false}
                    placeholder={[START_DATE, END_DATE]}
                    ranges={getOptions()}
                    data-testid="creationDate"
                    renderExtraFooter={() => MAXIMUM_PERIOD_TO_SELECT}
                    locale={translateDatePicker()}
                  ></RangePicker>
                </Form.Item>
              </Space>
            </S.DatePickerContainer>
            <S.ButtonSubmitFilterContainer>
              <Button className="primaryButton" size="large" onClick={onReportQuery} disabled={isLoadingRegisters}>
                {FILTER}
              </Button>
            </S.ButtonSubmitFilterContainer>
            <S.ButtonSubmitExportContainer>
              <Button className="secondaryButton" htmlType="submit" size="large" disabled={isLoadingRegisters} data-testid="submit-export" icon={<ExportFile />}>
                {EXPORT}
              </Button>
            </S.ButtonSubmitExportContainer>
          </S.FilterReportContainerSecondary>
        </S.ReportContainer>
      </Form>
      <ReportGrid
        items={items}
        totalItems={totalItems}
        reportChangeFieldValue={reportChangeFieldValue}
        reportFetchRegisterAsync={reportFetchRegisterAsync}
        pageIndex={pageIndex}
        columnsTable={ColumnsTable({ inboundDocumentsTypes, outboundDocumentsTypes })}
        isLoadingRegisters={isLoadingRegisters}
      />
    </Layout>
  );
};

export default RenderReport;
